import React, { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';

const ScheduleTracker = ({ selectedSchedule, onClose }) => {
  const [teams, setTeams] = useState([]);
  const [opponentMatrix, setOpponentMatrix] = useState({});

  const processScheduleData = useCallback(() => {
    const teamsSet = new Set();
    const opponents = {};

    selectedSchedule.schedule_data.forEach(week => {
      week.games?.forEach(game => {
        teamsSet.add(game.homeTeam);
        teamsSet.add(game.awayTeam);
      });
    });

    const teamsList = Array.from(teamsSet).sort();
    setTeams(teamsList);

    teamsList.forEach(team => {
      opponents[team] = {};
      teamsList.forEach(opponent => {
        opponents[team][opponent] = { home: 0, away: 0 };
      });
    });

    selectedSchedule.schedule_data.forEach(week => {
      week.games?.forEach(game => {
        const home = game.homeTeam;
        const away = game.awayTeam;
        if (opponents[home] && opponents[home][away]) {
          opponents[home][away].home += 1;
        }
        if (opponents[away] && opponents[away][home]) {
          opponents[away][home].away += 1;
        }
      });
    });

    setOpponentMatrix(opponents);
  }, [selectedSchedule]);

  useEffect(() => {
    if (selectedSchedule?.schedule_data) {
      processScheduleData();
    }
  }, [selectedSchedule, processScheduleData]);

  const getTotalGames = (team, opponent) => {
    if (!opponentMatrix[team] || !opponentMatrix[team][opponent]) return 0;
    const data = opponentMatrix[team][opponent];
    return data.home + data.away;
  };

  const getDisplayValue = (team, opponent) => {
    if (team === opponent) {
      let totalGames = 0;
      Object.keys(opponentMatrix[team] || {}).forEach(opp => {
        if (opp !== team) {
          totalGames += getTotalGames(team, opp);
        }
      });
      return totalGames;
    }

    const data = opponentMatrix[team]?.[opponent];
    if (!data) return '';
    if (data.home > 0 && data.away > 0) return `${data.home}/${data.away}`;
    if (data.home > 0) return data.home;
    if (data.away > 0) return data.away;
    return '';
  };

  const getCellClassName = (team, opponent) => {
    if (team === opponent) return 'diagonal-cell';
    const data = opponentMatrix[team]?.[opponent];
    if (!data) return '';
    if (data.home > 0 && data.away > 0) return 'both-games';
    if (data.home > 0) return 'home-game';
    if (data.away > 0) return 'away-game';
    return '';
  };

  return (
    <div className="schedule-tracker-modal-overlay" onClick={onClose}>
      <div className="schedule-tracker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tracker-header">
          <h2>Schedule Checker</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="tracker-content">
          <div className="matrix-wrapper">
            <div className="matrix-scroll-container">
              <table className="matrix-table">
                <thead>
                  <tr>
                    <th className="corner-cell"></th>
                    {teams.map(team => (
                      <th key={team} className="header-cell">{team}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {teams.map(team => (
                    <tr key={team}>
                      <td className="row-header">{team}</td>
                      {teams.map(opponent => (
                        <td
                          key={opponent}
                          className={`matrix-cell ${getCellClassName(team, opponent)}`}
                        >
                          {getDisplayValue(team, opponent)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <style jsx>{`
          .schedule-tracker-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.6);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10000;
            padding: 20px;
          }

          .schedule-tracker-modal {
            background: white;
            border-radius: 12px;
            width: 90vw;
            max-width: 1200px;
            height: min(90vh, 800px);
            max-height: 90vh;
            display: flex;
            flex-direction: column;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
            overflow: hidden;
          }

          .tracker-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px 24px;
            border-bottom: 2px solid #e5e7eb;
            background: white;
            flex-shrink: 0;
          }

          .tracker-header h2 {
            margin: 0;
            font-size: 22px;
            font-weight: 600;
            color: #111827;
          }

          .close-btn {
            background: none;
            border: none;
            padding: 8px;
            cursor: pointer;
            color: #6b7280;
            display: flex;
            align-items: center;
            border-radius: 6px;
            transition: all 0.2s;
          }

          .close-btn:hover {
            background: #f3f4f6;
            color: #111827;
          }

          .tracker-content {
            flex: 1;
            overflow: hidden;
            padding: 24px;
            min-height: 0;
            display: flex;
            flex-direction: column;
          }

          .matrix-wrapper {
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            overflow: hidden;
            flex: 1;
            min-height: 0;
            display: flex;
            flex-direction: column;
          }

          .matrix-scroll-container {
            overflow: auto;
            flex: 1;
            min-height: 0;
          }

          .matrix-table {
            width: 100%;
            border-collapse: collapse;
            background: white;
            font-size: 12px;
          }

          .matrix-table th,
          .matrix-table td {
            border: 1px solid #e5e7eb;
            text-align: center;
            padding: 8px 6px;
          }

          .corner-cell {
            background: #f9fafb;
            border: 1px solid #d1d5db;
            min-width: 100px;
            position: sticky;
            left: 0;
            z-index: 3;
          }

          .header-cell {
            background: #f9fafb;
            font-weight: 600;
            font-size: 12px;
            color: #374151;
            border: 1px solid #d1d5db;
            min-width: 50px;
            white-space: nowrap;
            position: sticky;
            top: 0;
            z-index: 2;
          }

          .row-header {
            background: #f9fafb;
            font-weight: 600;
            font-size: 12px;
            text-align: left;
            color: #374151;
            border: 1px solid #d1d5db;
            white-space: nowrap;
            min-width: 100px;
            padding-left: 12px;
            position: sticky;
            left: 0;
            z-index: 1;
          }

          .matrix-cell {
            font-size: 13px;
            color: #111827;
            font-weight: 500;
            min-width: 50px;
          }

          .diagonal-cell {
            background: #d1d5db;
            color: #111827;
            font-weight: 700;
          }

          .home-game { background: white; color: #111827; }
          .away-game { background: white; color: #111827; }
          .both-games { background: white; color: #111827; }

          .matrix-scroll-container::-webkit-scrollbar { width: 8px; height: 8px; }
          .matrix-scroll-container::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
          .matrix-scroll-container::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
          .matrix-scroll-container::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }

          .tracker-content::-webkit-scrollbar { width: 8px; }
          .tracker-content::-webkit-scrollbar-track { background: #f1f1f1; }
          .tracker-content::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
          .tracker-content::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }

          @media (max-width: 768px) {
            .schedule-tracker-modal { width: 95vw; max-height: 95vh; }
            .tracker-header { padding: 16px 20px; }
            .tracker-header h2 { font-size: 18px; }
            .tracker-content { padding: 16px 20px; }
            .matrix-table { font-size: 11px; }
            .matrix-table th, .matrix-table td { padding: 6px 4px; }
            .header-cell, .matrix-cell { min-width: 40px; }
            .corner-cell, .row-header { min-width: 80px; }
          }
        `}</style>
      </div>
    </div>
  );
};

export default ScheduleTracker;